import React, { useCallback } from 'react';
import { Box } from '@wix/design-system';
import { SpacingControl } from './SpacingControl';
import {
  GAP_CSS_PROPERTY_TYPES,
  PADDING_CSS_PROPERTY_TYPES,
} from '../../common/constants';
import { SpacingControlsProps } from '../types';
import { useSpacingPanel } from '../hooks/useSpacingPanel';

import { useTranslation } from '../../../useTranslation';
import { useBI } from '../../hooks/useBI';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';
import { GD_DATA_HOOKS } from '../../constants';

const formatValue = (
  value: string | number | null,
  percentageMode: boolean,
): string => {
  if (!value) {
    return '';
  }

  return percentageMode
    ? `${typeof value === 'number' ? value : parseInt(value, 10)}%`
    : `${value}`;
};

export const SpacingControls: React.FC<SpacingControlsProps> = ({ hosts }) => {
  const { trackValueUpdate } = useBI();
  const [t] = useTranslation();
  const paddingSpacingControl = useSpacingPanel(
    hosts.map((host) => host.host),
    PADDING_CSS_PROPERTY_TYPES,
  );

  const gapSpacingControl = useSpacingPanel(
    hosts.map((host) => host.host),
    GAP_CSS_PROPERTY_TYPES,
  );

  const trackValueUpdateCallback = (value: string) => {
    const [leadingHost, ...subsetHosts] = hosts;

    if (!leadingHost) {
      return;
    }

    trackValueUpdate(StylesDerivedNativeActions.spacingStyles, {
      prevValues: formatValue(value, paddingSpacingControl.isMixed),
      newValues: formatValue(value, paddingSpacingControl.isMixed),
      targetComponents: {
        leadingGroupInfo: {
          groupId: leadingHost.metadata.editorPointerId,
          displayName: leadingHost.metadata.rootDisplayName,
          componentType: leadingHost.metadata.extensionType,
        },
        subsetGroupInfos: subsetHosts.map((host) => ({
          groupId: host.metadata.editorPointerId,
          displayName: host.metadata.rootDisplayName,
        })),
      },
    });
  };

  const handlePaddingChange = (value: string) => {
    trackValueUpdateCallback(value);
    paddingSpacingControl.onChange(value);
  };

  const handleGapChange = (value: string) => {
    trackValueUpdateCallback(value);
    gapSpacingControl.onChange(value);
  };

  if (!hosts.length) {
    return null;
  }

  if (
    !paddingSpacingControl.isControlVisible &&
    !gapSpacingControl.isControlVisible
  ) {
    return null;
  }

  const paddingValue = paddingSpacingControl.isMixed
    ? paddingSpacingControl.percentageValue
    : paddingSpacingControl.sharedValue || '';

  const gapValue = gapSpacingControl.isMixed
    ? gapSpacingControl.percentageValue
    : gapSpacingControl.sharedValue || '';

  return (
    <Box direction="vertical" gap="4">
      {paddingSpacingControl.isControlVisible ? (
        <SpacingControl
          value={paddingValue}
          onChange={handlePaddingChange}
          percentageMode={paddingSpacingControl.isMixed}
          label={t('globalDesign.padding.controller.label')}
          dataHook={GD_DATA_HOOKS.SPACING_PADDING}
        />
      ) : null}
      {gapSpacingControl.isControlVisible ? (
        <SpacingControl
          value={gapValue}
          onChange={handleGapChange}
          percentageMode={gapSpacingControl.isMixed}
          label={t('globalDesign.spacing.controller.label')}
          dataHook={GD_DATA_HOOKS.SPACING_GAP}
        />
      ) : null}
    </Box>
  );
};
